import type { ICaaCustomer } from "../queries/customer-account-gql";
import type { ICustomerQuery, IOrdersEdgesNode } from "../queries/customer-gql";
import type { IProductDetailQuery } from "../queries/product-gql";
import type { ICart } from "../queries/cart-gql";

export const checkProductLimitReached = ({
  product,
  cart,
  customer,
}: {
  product: IProductDetailQuery["product"];
  cart: ICart["cart"];
  customer: ICustomerQuery["customer"] | null;
}): boolean => {
  // 상품 개수 제한
  const quantityLimitProduct = parseInt(
    product?.quantityLimitProduct?.value || "-1",
  );
  if (quantityLimitProduct > 0) {
    // 카트에 해당 상품이 담겨있는지 확인
    const cartLineItems = cart.lines.edges.filter(
      ({ node }) => product?.id === node.merchandise.product.id,
    );

    // 카트에 담긴 상품의 수량
    const cartLineItemCount = cartLineItems.reduce(
      (prev, curr) => prev + curr.node.quantity,
      0,
    );

    // 구매 내역에 있는지 확인 (패밀리세일 제외, currentQuantity로 취소/환불/부분환불 자동 반영)
    const itemInOrderHistory =
      customer?.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .flatMap(({ node }) => node.lineItems.edges)
        .filter(({ node }) => node.variant?.product.id === product?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce(
      (total, { node }) => total + (node.currentQuantity || 0),
      0,
    );

    return cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct;
  }

  return false;
};

export const checkVariantLimitReached = ({
  product,
  customer,
  selectedOption,
  cart,
}: {
  product: IProductDetailQuery["product"];
  customer: ICustomerQuery["customer"] | null;
  selectedOption: string;
  cart: ICart["cart"];
}): boolean => {
  // 상품 variant 개수 제한
  const quantityLimitVariant = parseInt(product?.quantityLimit?.value || "-1");
  if (quantityLimitVariant > 0) {
    const cartLineVariantItems = cart.lines.edges.filter(
      ({ node }) => selectedOption === node.merchandise.id,
    );
    const cartLineVariantsItemCount = cartLineVariantItems.reduce(
      (acc, curr) => acc + curr.node.quantity,
      0,
    );

    // 구매 내역에 있는지 확인 (패밀리세일 제외, currentQuantity로 취소/환불/부분환불 자동 반영)
    const itemInOrderHistory =
      customer?.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .flatMap(({ node }) => node.lineItems.edges)
        .filter(({ node }) => selectedOption === node.variant?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce(
      (total, { node }) => total + (node.currentQuantity || 0),
      0,
    );

    return (
      cartLineVariantsItemCount + totalOrderedProductCount >=
      quantityLimitVariant
    );
  }

  return false;
};

/**
 * classic checkProductLimitReached의 CAA 대응.
 *
 * classic과 차이:
 *   - 라인아이템 product 매칭: `variant.product.id` 대신 CAA의 flat `productId` 사용
 *   - currentQuantity(환불 반영 후 수량): CAA lineItem엔 필드가 없지만 `refundableQuantity`(quantity - 환불된 수량)가
 *     동일한 값을 제공한다. jp의 실제 환불은 Return(RMA) 없이 CS/오버셀링 건을 어드민에서 직접 환불하는 경우가
 *     대부분이라 Order.returns 기반 근사로는 이런 직접 환불이 전혀 반영되지 않는다 — refundableQuantity는 Return
 *     경유 여부와 무관하게 모든 환불을 반영하므로 이 필드를 그대로 사용
 *
 * product / cart는 여전히 classic Storefront에서 오는 값
 */
export const checkProductLimitReachedCaa = ({
  product,
  cart,
  customer,
}: {
  product: IProductDetailQuery["product"];
  cart: ICart["cart"];
  customer: ICaaCustomer | null;
}): boolean => {
  const quantityLimitProduct = parseInt(
    product?.quantityLimitProduct?.value || "-1",
  );
  if (quantityLimitProduct > 0) {
    const cartLineItems = cart.lines.edges.filter(
      ({ node }) => product?.id === node.merchandise.product.id,
    );
    const cartLineItemCount = cartLineItems.reduce(
      (prev, curr) => prev + curr.node.quantity,
      0,
    );

    const itemInOrderHistory =
      customer?.orders?.edges
        .filter(({ node: order }) => order.isfamilysale?.value !== "true")
        .flatMap(({ node: order }) => order.lineItems.edges)
        .filter(({ node: line }) => line.productId === product?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce(
      (total, { node: line }) => total + line.refundableQuantity,
      0,
    );

    return cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct;
  }

  return false;
};

/**
 * classic checkVariantLimitReached의 CAA 대응.
 * variant 매칭: `variant.id` 대신 CAA의 flat `variantId` 사용. currentQuantity 재현은 동일 방식(refundableQuantity).
 */
export const checkVariantLimitReachedCaa = ({
  product,
  customer,
  selectedOption,
  cart,
}: {
  product: IProductDetailQuery["product"];
  customer: ICaaCustomer | null;
  selectedOption: string;
  cart: ICart["cart"];
}): boolean => {
  const quantityLimitVariant = parseInt(product?.quantityLimit?.value || "-1");
  if (quantityLimitVariant > 0) {
    const cartLineVariantItems = cart.lines.edges.filter(
      ({ node }) => selectedOption === node.merchandise.id,
    );
    const cartLineVariantsItemCount = cartLineVariantItems.reduce(
      (acc, curr) => acc + curr.node.quantity,
      0,
    );

    const itemInOrderHistory =
      customer?.orders?.edges
        .filter(({ node: order }) => order.isfamilysale?.value !== "true")
        .flatMap(({ node: order }) => order.lineItems.edges)
        .filter(({ node: line }) => line.variantId === selectedOption) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce(
      (total, { node: line }) => total + line.refundableQuantity,
      0,
    );

    return (
      cartLineVariantsItemCount + totalOrderedProductCount >=
      quantityLimitVariant
    );
  }

  return false;
};
